/**
 * ApproveModal — Phase 07
 *
 * Modal for approving an engine review.
 * If there are verifier warnings, requires acknowledgement checkbox.
 *
 * dual_method + OUT OF TOLERANCE: also requires a court-facing "variance attribution"
 * (>=10 chars). It is stored on the SM1 engine_reviews row on approve (single source of
 * truth — no separate store) and gates the carrier exhibit's "reasonableness check". The
 * accountant is the attestor; the CPA name+license stamp is applied server-side from their
 * profile. A read-only AI DRAFT ("Suggested by analysis — draft, not attested") is offered
 * as optional scaffolding: it only fills the field on an explicit "Use this draft" click,
 * the accountant then edits and approves, and the AI never sets or signs the attribution.
 */

import React, { useState, useEffect, useRef } from 'react';
import Modal from '../../ui/Modal';
import { Button } from '../../ui/Button';
import CpaWorkpaperTextarea from '../../privilege/CpaWorkpaperTextarea';
import CpaPrivilegeWarning from '../../privilege/CpaPrivilegeWarning';
import { api } from '../../../utils/api';
import type { EngineId, VerifierReport, ApprovePayload } from '../../../types/engineReview';
import { ENGINE_NAMES } from '../../../types/engineReview';

// =============================================================================
// COMPONENT
// =============================================================================

/** dual_method variance context, supplied by the panel from the engine output. */
export interface ApproveVarianceContext {
  isWithinTolerance: boolean;
  /** Pre-formatted figures ("$161,996.73"); the panel owns the money format. */
  topDown?: string;
  bottomUp?: string;
  spread?: string;
  /** Percentage value, e.g. 6.3859 (rendered 2dp). */
  variancePct?: number;
}

const MIN_ATTR = 10;
const MAX_ATTR = 600;

// Wrap $/% tokens in DM Mono (financial figures), inline in the prompt sentence.
const _FIG_SPLIT = /(\$[\d,]+(?:\.\d+)?|\d+(?:\.\d+)?\s?%)/g;
const _FIG_TEST = /^(?:\$[\d,]+(?:\.\d+)?|\d+(?:\.\d+)?\s?%)$/;
function renderWithFigures(text: string) {
  return text.split(_FIG_SPLIT).map((part, i) =>
    _FIG_TEST.test(part)
      ? <span key={i} className="cs-figure" style={{ fontSize: 'inherit' }}>{part}</span>
      : <span key={i}>{part}</span>,
  );
}

export interface ApproveModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (payload: ApprovePayload) => void;
  engineId: EngineId;
  verifierReport?: VerifierReport;
  isSubmitting?: boolean;
  /** dual_method only: drives the required variance-attribution field when out of tolerance. */
  varianceContext?: ApproveVarianceContext;
  /** Report UUID — enables the read-only AI draft for the required attribution (fail-open). */
  reportId?: string;
}

/** Read-only AI-draft state for the required attribution. 'unavailable' → the field just
 *  works manually (fail-open); the draft is optional scaffolding, never a gate. */
type SuggestionState = 'idle' | 'loading' | 'ready' | 'unavailable';

export default function ApproveModal({
  isOpen,
  onClose,
  onSubmit,
  engineId,
  verifierReport,
  isSubmitting = false,
  varianceContext,
  reportId,
}: ApproveModalProps) {
  const [note, setNote] = useState('');
  const [acknowledges, setAcknowledges] = useState(false);
  const [attribution, setAttribution] = useState('');
  const [suggestion, setSuggestion] = useState<string | null>(null);
  const [suggestionState, setSuggestionState] = useState<SuggestionState>('idle');
  // Adoption flag: once the draft is inserted, re-insert is disabled so a stray click can't
  // clobber the CPA's own edits one click before Approve (Fable integrity amendment).
  const [draftInserted, setDraftInserted] = useState(false);
  const attributionRef = useRef<HTMLTextAreaElement>(null);

  const insertDraft = () => {
    if (!suggestion) return;
    setAttribution(suggestion);
    setDraftInserted(true);
    // Hand ownership to the human: focus the field so the next keystroke edits their wording.
    setTimeout(() => attributionRef.current?.focus(), 0);
  };

  const engineName = ENGINE_NAMES[engineId];

  // Check for warnings (not blockers - blockers prevent opening the modal)
  const warnings = (verifierReport?.checks_failed ?? []).filter(
    (check) => check.severity === 'warning',
  );
  const hasWarnings = warnings.length > 0;

  // dual_method + out-of-tolerance → a court-facing attribution is REQUIRED to approve.
  const requiresAttribution =
    engineId === 'dual_method' && varianceContext?.isWithinTolerance === false;
  const attrTrimmed = attribution.trim();
  const attributionValid = requiresAttribution
    ? attrTrimmed.length >= MIN_ATTR && attrTrimmed.length <= MAX_ATTR
    : true;

  const canSubmit = (!hasWarnings || acknowledges) && attributionValid;
  // Disabled specifically because the attribution is missing/short → dormant orange
  // (still the accent action, waiting), never a dead gray.
  const dormant = requiresAttribution && !attributionValid && !isSubmitting;

  const vc = varianceContext;
  const promptText =
    requiresAttribution && vc?.topDown && vc?.bottomUp && vc?.spread &&
    vc?.variancePct != null && Number.isFinite(vc.variancePct)
      ? `Top-down (${vc.topDown}) and bottom-up (${vc.bottomUp}) differ by ${vc.spread} (${vc.variancePct.toFixed(2)}%). What accounts for the spread?`
      : 'What accounts for the difference between the two methods? Record the driver(s) of the spread.';

  // Read-only AI draft: fetched lazily only when the modal opens for an out-of-tolerance
  // dual_method (so no Haiku call fires just from viewing the panel). Fail-open — any
  // error/null/rejected draft → 'unavailable' and the accountant types it themselves. The
  // draft only POPULATES the textarea on an explicit click; the accountant edits and approves,
  // and the stored attestation is stamped server-side from THEIR identity — the AI never attests.
  useEffect(() => {
    if (!isOpen || !requiresAttribution || !reportId) return;
    let ignore = false;
    setSuggestion(null);
    setSuggestionState('loading');
    (async () => {
      try {
        const res = await api.get<{ suggestion?: string | null }>(
          `/api/v1/engine-review/${reportId}/variance-attribution/suggestion`,
        );
        if (ignore) return;
        const s = (res?.suggestion ?? '').trim();
        if (s) { setSuggestion(s); setSuggestionState('ready'); }
        else { setSuggestionState('unavailable'); }
      } catch {
        if (!ignore) setSuggestionState('unavailable');
      }
    })();
    return () => { ignore = true; };
  }, [isOpen, requiresAttribution, reportId]);

  const handleSubmit = () => {
    if (!canSubmit) return;
    onSubmit({
      note: note.trim() || undefined,
      acknowledges_verifier_warnings: hasWarnings ? acknowledges : undefined,
      variance_attribution: requiresAttribution ? attrTrimmed : undefined,
    });
  };

  const handleClose = () => {
    setNote('');
    setAcknowledges(false);
    setAttribution('');
    setSuggestion(null);
    setSuggestionState('idle');
    setDraftInserted(false);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={`Approve ${engineName}`}
      size="md"
      footer={
        <div className="flex justify-end gap-3">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isSubmitting}
            data-testid="approve-modal-cancel"
          >
            Cancel
          </Button>
          <Button
            variant="default"
            onClick={handleSubmit}
            disabled={!canSubmit || isSubmitting}
            data-testid="approve-modal-submit"
            title={dormant ? `Enter a variance attribution of at least ${MIN_ATTR} characters to approve` : undefined}
            style={
              dormant
                ? {
                    backgroundColor: 'var(--color-needs-human-tint)',
                    border: '1px solid var(--color-needs-human-tint-bd)',
                    color: 'var(--color-needs-human-text)',
                    opacity: 1,
                    cursor: 'not-allowed',
                  }
                : undefined
            }
          >
            {isSubmitting ? 'Approving...' : 'Approve'}
          </Button>
        </div>
      }
    >
      <div className="space-y-4" data-testid="approve-modal">
        {/* No warnings - simple confirmation */}
        {!hasWarnings && (
          <p style={{ color: 'var(--color-text-secondary)' }}>
            You are about to approve the <strong>{engineName}</strong> calculation.
            This action will be recorded in the attestation history.
          </p>
        )}

        {/* Warnings present - require acknowledgement */}
        {hasWarnings && (
          <>
            <div className="border rounded-lg p-4" style={{ backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' }}>
              <div className="flex items-start gap-3">
                <svg
                  className="w-5 h-5 flex-shrink-0 mt-0.5"
                  style={{ color: 'var(--color-brand)' }}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
                <div>
                  <h4 className="font-medium" style={{ color: 'var(--color-needs-human-text)' }}>
                    Verifier Warnings
                  </h4>
                  <p className="mt-1 text-sm" style={{ color: 'var(--color-needs-human-text)' }}>
                    The following warnings were found during verification:
                  </p>
                  <ul className="mt-2 space-y-1">
                    {warnings.map((warning) => (
                      <li
                        key={warning.check_id}
                        className="text-sm flex items-start gap-2"
                        style={{ color: 'var(--color-needs-human-text)' }}
                      >
                        <span className="mt-0.5" style={{ color: 'var(--color-brand)' }}>-</span>
                        <span>
                          <strong>{warning.check_name}:</strong> {warning.message}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={acknowledges}
                onChange={(e) => setAcknowledges(e.target.checked)}
                className="mt-1 h-4 w-4 rounded"
                style={{ borderColor: 'var(--color-border)', accentColor: 'var(--color-brand)' }}
                data-testid="approve-modal-acknowledge"
              />
              <span className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                I acknowledge these warnings and confirm that the{' '}
                <strong>{engineName}</strong> calculation is acceptable for
                attestation purposes.
              </span>
            </label>
          </>
        )}

        {/* dual_method out-of-tolerance: REQUIRED court-facing variance attribution.
            The one CPA privilege banner for the modal lives here (the note below sets
            showBanner={false} to dedupe). */}
        {requiresAttribution && (
          <div
            data-testid="approve-modal-variance"
            style={{ border: '1px solid var(--color-border)', background: 'var(--color-bg-base)', padding: 16, borderRadius: 2 }}
          >
            <CpaPrivilegeWarning testId="approve-modal-variance-privilege-warning" />
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginTop: 4 }}>
              <span className="cs-h3" style={{ color: 'var(--color-text-primary)' }}>Variance attribution</span>
              <span className="cs-label" style={{ color: 'var(--color-text-dim)', textTransform: 'uppercase', fontSize: 11, letterSpacing: '0.06em' }}>Required</span>
            </div>
            <p
              className="cs-body"
              data-testid="approve-modal-variance-prompt"
              style={{ color: 'var(--color-text-secondary)', marginTop: 8 }}
            >
              {renderWithFigures(promptText)}
            </p>

            {/* Read-only AI draft (optional scaffolding). Neutral, NOT the accent — orange
                stays on Approve. Clearly labelled as a draft the accountant still owns: it only
                fills the field below on an explicit click, then the accountant edits + attests. */}
            {suggestionState === 'loading' && (
              <p
                className="cs-meta"
                data-testid="approve-modal-variance-suggestion-loading"
                style={{ color: 'var(--color-text-dim)', marginTop: 10 }}
              >
                Drafting a suggestion from the calculation notes…
              </p>
            )}
            {suggestionState === 'ready' && suggestion && (
              <div
                data-testid="approve-modal-variance-suggestion"
                style={{ border: '1px dashed var(--color-border)', background: 'var(--color-bg-surface)', padding: '10px 12px', borderRadius: 2, marginTop: 10 }}
              >
                <span
                  className="cs-label"
                  data-testid="approve-modal-variance-suggestion-label"
                  style={{ color: 'var(--color-text-dim)', textTransform: 'uppercase', fontSize: 11, letterSpacing: '0.06em' }}
                >
                  {draftInserted
                    ? 'Inserted — this is now your wording; edit as needed'
                    : 'Draft — generated from the engine’s reconciliation notes. Not attested. Yours to edit'}
                </span>
                <p
                  className="cs-body"
                  data-testid="approve-modal-variance-suggestion-text"
                  style={{ color: 'var(--color-text-muted)', marginTop: 6 }}
                >
                  {renderWithFigures(suggestion)}
                </p>
                <button
                  type="button"
                  data-testid="approve-modal-variance-suggestion-use"
                  onClick={insertDraft}
                  disabled={draftInserted}
                  className="cs-label"
                  style={{
                    marginTop: 8, padding: '4px 10px', borderRadius: 2,
                    cursor: draftInserted ? 'default' : 'pointer',
                    border: '1px solid var(--color-border)', background: 'transparent',
                    color: draftInserted ? 'var(--color-text-dim)' : 'var(--color-text-primary)',
                    opacity: draftInserted ? 0.6 : 1,
                  }}
                  // Neutral-only hover: brighten the border, never orange (accent stays on Approve).
                  onMouseEnter={(e) => { if (!draftInserted) e.currentTarget.style.borderColor = 'var(--color-text-muted)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--color-border)'; }}
                >
                  {draftInserted ? 'Inserted' : 'Insert draft'}
                </button>
              </div>
            )}

            <div style={{ position: 'relative', marginTop: 8 }}>
              <textarea
                id="approve-variance-attribution"
                ref={attributionRef}
                data-testid="approve-modal-variance-attribution"
                value={attribution}
                onChange={(e) => setAttribution(e.target.value)}
                rows={3}
                maxLength={MAX_ATTR}
                aria-required
                aria-describedby="approve-modal-variance-attest-line"
                placeholder="e.g. Payroll continuation accounts for the spread; ledger reclass immaterial."
                className="cs-body w-full"
                style={{
                  border: '1px solid var(--color-border)',
                  background: 'var(--color-bg-surface)',
                  color: 'var(--color-text-primary)',
                  outline: 'none',
                  resize: 'none',
                  padding: '10px 12px 26px 12px',
                }}
                onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--color-brand)'; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = 'var(--color-border)'; }}
              />
              <div
                className="cs-figure"
                style={{
                  position: 'absolute', bottom: 8, right: 12, fontSize: 11,
                  color: attrTrimmed.length >= MAX_ATTR ? 'var(--color-text-secondary)' : 'var(--color-text-dim)',
                  fontWeight: attrTrimmed.length >= MAX_ATTR ? 600 : 400, pointerEvents: 'none',
                }}
              >
                {attrTrimmed.length} / {MAX_ATTR}
              </div>
            </div>
            <p
              id="approve-modal-variance-attest-line"
              data-testid="approve-modal-variance-attest-line"
              className="cs-meta"
              style={{ color: 'var(--color-text-dim)', marginTop: 8 }}
            >
              When you approve, this becomes your attestation — as written, under your name
              and CPA license.
            </p>
          </div>
        )}

        {/* Optional note — wrapped per Phase 10 Task 10.5. Banner deduped when the
            attribution block above already shows one. */}
        <CpaWorkpaperTextarea
          id="approve-note"
          label="Note (optional)"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Add any notes about this approval..."
          rows={3}
          showBanner={!requiresAttribution}
          className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2"
          style={{ borderColor: 'var(--color-border)' }}
          data-testid="approve-modal-note"
        />
      </div>
    </Modal>
  );
}

// =============================================================================
// EXPORTS
// =============================================================================

export { ApproveModal };
